import { api } from '@/api';
import { getLanguage } from '@/i18n';
import type {
  BookingOfferDTO,
  MarkVoucherForPaymentPayloadDTO,
  MultiRideAccountDTO,
  PlaceSelectionDTO,
  PromotionCodeDTO,
  ReductionCardAccountDTO,
  TravelPassAccountDTO,
  ValidateAdyenCheckoutPaymentDetailsBodyDTO,
} from '@/types/dto/agent';
import type { BookingFulfillmentStorageData } from '@/types/storage';
import {
  findTicketFulfillmentWithDownloadLink,
  getBookingFulfillments,
  getPkpassFulfillments,
  hasMultipleTicketFulfillmentsWithDownloadLink,
  isPkpassFulfillment,
} from '@/utils/booking';
import type { SearchFormValues } from '@packages/webshop-search-widget';
import type { AxiosRequestConfig } from 'axios';
import download from 'downloadjs';

const downloadRequestConfig = (): AxiosRequestConfig => ({
  responseType: 'blob',
  headers: {
    'Accept-Language': getLanguage(),
  },
});

const getResponseHeader = (
  headers: Record<string, unknown> | undefined,
  name: string
): string | undefined => {
  if (!headers) {
    return undefined;
  }

  const value = headers[name] ?? headers[name.toLowerCase()];

  return typeof value === 'string' ? value : undefined;
};

const parseContentDispositionFilename = (
  contentDisposition: string
): string | undefined => {
  const filenameStarMatch = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?([^;\s]+)/i
  );

  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1].replace(/["']/g, ''));
    } catch {
      // Fall through to filename=.
    }
  }

  const filenameMatch = contentDisposition.match(
    /filename\s*=\s*("([^"]+)"|([^;\s]+))/i
  );

  if (filenameMatch) {
    return (filenameMatch[2] ?? filenameMatch[3])?.replace(/^["']|["']$/g, '');
  }

  return undefined;
};

const getDownloadFilename = (
  response: { headers?: Record<string, unknown> },
  fallbackFilename: string
): string => {
  const contentDisposition = getResponseHeader(
    response.headers,
    'content-disposition'
  );

  if (contentDisposition) {
    const filename = parseContentDispositionFilename(contentDisposition);

    if (filename) {
      return filename;
    }
  }

  return fallbackFilename;
};

const downloadBlobResponse = (
  response: { data: Blob; headers?: Record<string, unknown> },
  fallbackFilename: string,
  mimeType: string
) => {
  download(
    response.data,
    getDownloadFilename(response, fallbackFilename),
    mimeType
  );
};

const downloadFulfillmentDocument = async (downloadLink: string) => {
  const retailerPath = downloadLink.match(/\/retailer(\/[^#]*)/)?.[1];

  if (retailerPath) {
    return api.retailerApi.get(retailerPath, downloadRequestConfig());
  }

  return api.agentApi.get(downloadLink, downloadRequestConfig());
};

const fulfillmentMatchesDocumentType = (
  fulfillment: BookingFulfillmentStorageData,
  documentType: 'PKPASS' | 'TICKET'
): boolean =>
  documentType === 'PKPASS'
    ? isPkpassFulfillment(fulfillment)
    : fulfillment.documentType === 'TICKET' &&
      !isPkpassFulfillment(fulfillment);

export const downloadBookingTickets = async (
  bookingId: string,
  bookingNumber?: string,
  fulfillments?: Array<BookingFulfillmentStorageData>
) => {
  let resolvedFulfillments = fulfillments;
  let ticketFulfillment = findTicketFulfillmentWithDownloadLink(fulfillments);

  if (!ticketFulfillment?.downloadLink) {
    try {
      const booking = await fetchBooking(bookingId);
      resolvedFulfillments = getBookingFulfillments(booking);
      ticketFulfillment =
        findTicketFulfillmentWithDownloadLink(resolvedFulfillments);
    } catch {
      // Fall through to Documents_GetBookingTickets when the refetch fails.
    }
  }

  const useCombinedTicketsApi =
    hasMultipleTicketFulfillmentsWithDownloadLink(resolvedFulfillments);

  if (!useCombinedTicketsApi && ticketFulfillment?.downloadLink) {
    try {
      const response = await downloadFulfillmentDocument(
        ticketFulfillment.downloadLink
      );

      downloadBlobResponse(
        response,
        `Tickets_${bookingNumber}`,
        ticketFulfillment.documentFormat ?? 'pdf'
      );
      return;
    } catch {
      // Expired or invalid download links should fall back to the API endpoint.
    }
  }

  const response = await api.agentApi.Documents_GetBookingTickets(
    {
      includeAll: true,
      bookingId,
    },
    {},
    {
      responseType: 'blob',
      headers: {
        'Accept-Language': getLanguage(),
      },
    }
  );

  downloadBlobResponse(response, `Tickets_${bookingNumber}`, 'pdf');
};

export const downloadBookingPkpass = async (
  bookingId: string,
  bookingNumber?: string,
  fulfillments?: Array<BookingFulfillmentStorageData>
) => {
  let pkpassFulfillments = getPkpassFulfillments(fulfillments);

  if (!pkpassFulfillments.some(({ downloadLink }) => downloadLink)) {
    try {
      const booking = await fetchBooking(bookingId);
      pkpassFulfillments = getPkpassFulfillments(
        getBookingFulfillments(booking)
      );
    } catch {
      // Fall through to Documents_GetDocument when the refetch fails.
    }
  }

  if (!pkpassFulfillments.length) {
    pkpassFulfillments = getPkpassFulfillments(fulfillments);
  }

  if (!pkpassFulfillments.length) {
    throw new Error('No PKPASS fulfillments available');
  }

  await Promise.all(
    pkpassFulfillments.map((fulfillment) =>
      downloadTicketDocumentByType(fulfillment, 'PKPASS', bookingNumber)
    )
  );
};

export const downloadTicketDocumentByType = async (
  fulfillment: BookingFulfillmentStorageData,
  documentType: 'PKPASS' | 'TICKET',
  bookingNumber?: string
) => {
  const [filename, mimeType] =
    documentType === 'PKPASS'
      ? [`Tickets_${bookingNumber}.pkpass`, 'application/vnd.apple.pkpass']
      : [`Tickets_${bookingNumber}.pdf`, 'application/pdf'];

  if (
    fulfillment.downloadLink &&
    fulfillmentMatchesDocumentType(fulfillment, documentType)
  ) {
    try {
      const response = await downloadFulfillmentDocument(
        fulfillment.downloadLink
      );

      if (response.data instanceof Blob && response.data.size > 0) {
        downloadBlobResponse(
          response,
          filename,
          fulfillment.documentFormat ?? mimeType
        );
        return;
      }
    } catch {
      // Expired or invalid download links should fall back to the API endpoint.
    }
  }

  const response = await api.agentApi.Documents_GetDocument(
    {
      fulfillmentId: fulfillment.id,
      documentType,
      ...(typeof fulfillment.isOutBound === 'boolean' && {
        isForInbound: !fulfillment.isOutBound,
      }),
    },
    {},
    {
      responseType: 'blob',
      headers: {
        'Accept-Language': getLanguage(),
      },
    }
  );

  if (response.data instanceof Blob && response.data.size > 0) {
    downloadBlobResponse(
      { data: response.data, headers: response.headers },
      filename,
      mimeType
    );
  } else {
    throw new Error('Empty or invalid document received');
  }
};

export const createBooking = async (
  offers: Array<BookingOfferDTO>,
  passengers: SearchFormValues['passengers'],
  promotionCodes?: Array<PromotionCodeDTO>
) => {
  return (
    await api.agentApi.Bookings_CreateBooking(null, {
      offers,
      passengerSpecifications: passengers.map(
        ({ age, externalReference, type }) => ({
          age,
          externalReference,
          type,
        })
      ),
      promotionCodes,
    })
  ).data;
};

export const fetchBooking = async (id: string | null | undefined) => {
  const data = (await api.agentApi.Bookings_GetBooking(id)).data;

  return {
    ...data,
    passengers: data.passengers?.sort(({ age }) => (!age ? -1 : 0)),
  };
};

export const fetchPlaceAvailability = async (
  bookingId: string,
  reservationId: string
) => {
  return (
    await api.agentApi.Bookings_GetAvailabilitiesPlaceMap({
      bookingId,
      reservationId,
    })
  ).data;
};

export const fetchFloorPlanIcons = async () => {
  return (await api.agentApi.FloorPlanIcons_GetFloorPlanIcons()).data;
};

export const updatePlaceSelections = async (
  bookingId: string,
  placeSelections: Array<PlaceSelectionDTO>
) => {
  return (
    await api.agentApi.Bookings_UpdateBooking(bookingId, {
      placeSelections,
    })
  ).data;
};

export const undoSeatSelection = async (
  bookingId: string,
  placeSelectionFeeId: string
) => {
  return (
    await api.agentApi.Bookings_DeletePlaceSelectionFee({
      bookingId,
      id: placeSelectionFeeId,
    })
  ).data;
};

export const validatePromoCode = async (code: string) =>
  (
    await api.agentApi.ValidatePromotionCode_ValidatePromotionCode(null, {
      code,
    })
  ).data;

export const addPromoCodes = async (
  bookingId: string,
  promotionCodes: Array<PromotionCodeDTO>
) =>
  (
    await api.agentApi.Bookings_UpdateBooking(bookingId, {
      promotionCodes,
    })
  ).data;

export const validateVoucher = async (
  voucherCode: string,
  bookingId: string
) => {
  return (
    await api.agentApi.Payments_ValidateVoucherForBooking(
      { bookingId, voucherCode },
      {
        offerPartIds: [],
      }
    )
  ).data;
};

export const addVoucherToBooking = async (
  voucherCode: string,
  bookingId: string,
  paidAmount: MarkVoucherForPaymentPayloadDTO['paidAmount']
) => {
  return (
    await api.agentApi.Payments_MarkVoucherForPayment(
      { bookingId, voucherCode },
      { offerPartIds: [], paidAmount }
    )
  ).data;
};

export const removeVoucherFromBooking = async (
  voucherCode: string,
  bookingId: string
) => {
  return (
    await api.agentApi.Payments_DeleteVoucherMarkedForPayment(
      { bookingId, voucherCode },
      { offerPartIds: [] }
    )
  ).data;
};

export const fetchIssuer = async ({
  controlNumber,
  issuer,
}: {
  controlNumber: string;
  issuer: string;
}) => {
  // as { travelAccount?: ... } can be removed once
  // TurnitOsdmContractsTravelAccountsTravelAccountResponse.travelAccount?: null & (...)
  // is changed to
  // TurnitOsdmContractsTravelAccountsTravelAccountResponse.travelAccount?: (...)
  // current schema causes 'travelAccount' to be type 'never'
  return (
    await api.agentApi.TravelAccounts_GetTravelAccount({
      Issuer: issuer,
      TravelAccount: controlNumber,
      Embed: 'ALL',
      cancelledIncluded: true,
    })
  ).data as {
    travelAccount?:
      | MultiRideAccountDTO
      | TravelPassAccountDTO
      | ReductionCardAccountDTO;
  };
};

export const validateAdyenCheckoutPaymentDetails = async (
  bookingId: string,
  body: ValidateAdyenCheckoutPaymentDetailsBodyDTO
) => {
  return await api.agentApi.Payments_ValidateAdyenCheckoutPaymentDetails(
    { bookingId },
    body
  );
};

export const getBookingPaymentStatus = async (
  bookingId: string,
  isForExchangeOperation = false
) => {
  const response = await api.agentApi.Payments_GetBookingPaymentStatus({
    bookingId,
    ...(isForExchangeOperation ? { isForExchangeOperation: true } : {}),
  });

  return response.data.status;
};

export const cancelBookingPaymentAttempts = async (bookingId: string) => {
  return await api.agentApi.Payments_CancelBookingPaymentAttempts({
    bookingId,
  });
};
